// @ts-nocheck
// AppDialog — the WeSafe standard dialog.
//
// MUI's <Dialog> is a low-level primitive: it has no built-in close button and
// it dismisses on backdrop click / Escape by default. Every dialog in the app
// wants the same three things, so they live here once instead of being
// hand-rolled per dialog:
//
//   1. A close (X) button in the top-right corner.
//   2. No dismissal on outside click / Escape (only the X or an action button
//      closes it). Pass `dismissable` to opt back into MUI's default.
//   3. Full-screen on phones so content + the close button never overflow.
//
// Usage:
//   <AppDialog open={open} onClose={onClose} title="Share documents"
//     maxWidth="sm" actions={<><Button onClick={onClose}>Cancel</Button>…</>}>
//     …content…
//   </AppDialog>
import { ReactNode } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Box,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Icon } from '@iconify/react';
import closeFill from '@iconify/icons-eva/close-fill';
import { dialogCloseGuard } from '../utils/dialog';

type Props = {
  open: boolean;
  onClose: () => void;
  /** Title text or node. Rendered with h6 styling when a string. */
  title?: ReactNode;
  /** Optional muted line beneath the title. */
  subtitle?: ReactNode;
  /** Rendered inline after the title (e.g. a status chip / back button). */
  titleStart?: ReactNode;
  children?: ReactNode;
  /** Content for the footer. Omit for dialogs with no action row. */
  actions?: ReactNode;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
  fullWidth?: boolean;
  /** Go full-screen on phones (default true). */
  fullScreenOnMobile?: boolean;
  /** Show divider lines around the content area (default true). */
  dividers?: boolean;
  /** Render the X close button (default true). */
  showClose?: boolean;
  /** Disable the X (e.g. while a request is in flight). */
  disableClose?: boolean;
  /** Allow backdrop-click / Escape dismissal (default false). */
  dismissable?: boolean;
  contentSx?: object;
  /** Extra props forwarded to the underlying MUI Dialog. */
  DialogProps?: object;
  PaperProps?: object;
};

export default function AppDialog({
  open,
  onClose,
  title,
  subtitle,
  titleStart,
  children,
  actions,
  maxWidth = 'sm',
  fullWidth = true,
  fullScreenOnMobile = true,
  dividers = true,
  showClose = true,
  disableClose = false,
  dismissable = false,
  contentSx,
  DialogProps,
  PaperProps,
}: Props) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm')) && fullScreenOnMobile;

  return (
    <Dialog
      open={open}
      onClose={dismissable ? onClose : dialogCloseGuard(onClose)}
      fullScreen={fullScreen}
      fullWidth={fullWidth}
      maxWidth={maxWidth}
      PaperProps={PaperProps}
      {...DialogProps}
    >
      {(title || titleStart || showClose) && (
        <DialogTitle sx={{ pr: showClose ? 6 : 3 }}>
          {titleStart}
          {typeof title === 'string' ? (
            title
          ) : (
            <Box sx={{ minWidth: 0 }}>{title}</Box>
          )}
          {subtitle && (
            <Typography variant="body2" component="div" sx={{ color: 'text.secondary', mt: 0.5 }}>
              {subtitle}
            </Typography>
          )}
          {showClose && (
            <IconButton
              aria-label="close"
              onClick={onClose}
              disabled={disableClose}
              sx={{ position: 'absolute', right: 8, top: 8, color: 'text.secondary' }}
            >
              <Icon icon={closeFill} width={20} height={20} />
            </IconButton>
          )}
        </DialogTitle>
      )}
      <DialogContent dividers={dividers} sx={contentSx}>
        {children}
      </DialogContent>
      {actions && <DialogActions>{actions}</DialogActions>}
    </Dialog>
  );
}
